import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  Mail, 
  MessageSquare,
  AlertCircle,
  DollarSign,
  MapPin,
  FileText,
  Truck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HAPPY_COUPLE = "https://images.unsplash.com/photo-1758523671087-b256bbbca475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNvdXBsZSUyMG1vdmluZyUyMGludG8lMjBuZXclMjBhcGFydG1lbnQlMjBib3hlc3xlbnwwfHx8fDE3Njk4NzgxMzR8MA&ixlib=rb-4.1.0&q=85";

const concernOptions = [
  { value: "budget", label: "Budget concerns", icon: DollarSign },
  { value: "neighborhood", label: "Finding the right neighborhood", icon: MapPin },
  { value: "application", label: "Application process", icon: FileText },
  { value: "moving", label: "Moving logistics", icon: Truck }
];

export default function ThankYouPage() {
  const navigate = useNavigate();
  const [leadId, setLeadId] = useState(null);
  const [leadName, setLeadName] = useState("");
  const [selectedConcern, setSelectedConcern] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    const storedId = sessionStorage.getItem("leadId");
    const storedName = sessionStorage.getItem("leadName");
    
    if (!storedId) {
      navigate("/");
      return;
    }
    
    setLeadId(storedId);
    setLeadName(storedName || "");
  }, [navigate]);

  const handleSubmit = async () => {
    if (!selectedConcern) {
      toast.error("Please select your biggest concern");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const updateData = {
        concern: selectedConcern,
        sms_opt_in: smsOptIn
      };
      
      if (smsOptIn && phone) {
        updateData.phone = phone;
      }
      
      await axios.patch(`${API}/leads/${leadId}`, updateData);
      toast.success("Thanks for sharing! We'll personalize your experience.");
      setHasSubmitted(true);
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!leadId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      <div className="max-w-2xl w-full relative z-10">
        {!hasSubmitted ? (
          <div className="space-y-8 animate-fadeInUp">
            {/* Success Message */}
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#84A98C]/20 mb-4">
                <CheckCircle2 className="w-10 h-10 text-[#84A98C]" />
              </div>
              
              <h1 
                data-testid="thank-you-title"
                className="text-3xl sm:text-4xl font-bold text-white"
              >
                You're All Set, {leadName}!
              </h1>
              
              <p className="text-[#F1F5F9]/70 text-lg max-w-md mx-auto">
                Check your inbox for the Sanctuary Seeker's Checklist. 
                <span className="block text-sm mt-2 text-[#F1F5F9]/50">
                  (Don't forget to check spam/promotions if you don't see it)
                </span>
              </p>
              
              <div className="flex items-center justify-center gap-2 text-[#84A98C] text-sm">
                <Mail className="w-4 h-4" />
                <span>Email sent with PDF attachment</span>
              </div>
            </div>
            
            {/* Segmentation Question */}
            <div className="glass-card p-8 bg-[#1E3A5F]/50">
              <h2 
                data-testid="segmentation-title"
                className="text-xl font-semibold text-white mb-2"
              >
                One Quick Question
              </h2>
              <p className="text-[#F1F5F9]/70 mb-6 text-sm">
                What's your biggest apartment hunting worry right now?
              </p>
              
              <RadioGroup 
                value={selectedConcern} 
                onValueChange={setSelectedConcern}
                className="space-y-3"
              >
                {concernOptions.map((option) => (
                  <div
                    key={option.value}
                    data-testid={`concern-option-${option.value}`}
                    className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-colors border ${
                      selectedConcern === option.value 
                        ? "bg-[#E07A5F]/20 border-[#E07A5F]" 
                        : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                    onClick={() => setSelectedConcern(option.value)}
                  >
                    <RadioGroupItem 
                      value={option.value} 
                      id={option.value}
                      className="border-white/30 text-[#E07A5F]"
                    />
                    <option.icon className={`w-5 h-5 ${
                      selectedConcern === option.value ? "text-[#E07A5F]" : "text-[#84A98C]"
                    }`} />
                    <Label 
                      htmlFor={option.value}
                      className="text-white cursor-pointer flex-1"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            {/* SMS Opt-in */}
            <div className="glass-card p-6 bg-[#1E3A5F]/50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 text-[#84A98C] mt-1" />
                  <div>
                    <h3 className="text-white font-medium">
                      Want text alerts for new listings?
                    </h3>
                    <p className="text-[#F1F5F9]/50 text-sm mt-1">
                      Get notified about apartments that match your criteria
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="sms-opt-in-switch"
                  checked={smsOptIn}
                  onCheckedChange={setSmsOptIn}
                />
              </div>
              
              {smsOptIn && (
                <div className="mt-4 animate-fadeIn">
                  <Input
                    data-testid="phone-input"
                    type="tel"
                    placeholder="Your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="input-field bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
              )}
            </div>
            
            {/* Submit Button */}
            <div className="text-center space-y-4">
              <Button
                data-testid="submit-preferences-button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-primary bg-[#E07A5F] hover:bg-[#C66A50] rounded-full px-12 py-6 text-lg"
              >
                {isSubmitting ? "Saving..." : "Save My Preferences"}
              </Button>
              
              <button
                data-testid="skip-button"
                onClick={() => setHasSubmitted(true)}
                className="block mx-auto text-[#F1F5F9]/50 text-sm hover:text-[#F1F5F9]/70 transition-colors"
              >
                Skip for now
              </button>
            </div>
          </div>
        ) : (
          /* Final Success State */
          <div className="text-center space-y-8 animate-fadeInUp">
            <div className="relative max-w-sm mx-auto">
              <img
                src={HAPPY_COUPLE}
                alt="Happy couple moving in"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            
            <div className="space-y-4">
              <h2 
                data-testid="final-success-title"
                className="text-3xl font-bold text-white"
              >
                You're Ready to Find Your Nest!
              </h2>
              <p className="text-[#F1F5F9]/70 max-w-md mx-auto">
                Your checklist is in your inbox. Print it out and bring it on every apartment tour!
              </p>
            </div>
            
            <div className="glass-card p-6 inline-flex items-center gap-3 bg-[#84A98C]/10">
              <AlertCircle className="w-5 h-5 text-[#84A98C]" />
              <span className="text-[#F1F5F9]/80 text-sm">
                Need personalized help? Reply to our email anytime!
              </span>
            </div>
            
            <Button
              data-testid="back-home-button"
              onClick={() => window.location.href = "https://linktr.ee/urbannestquest"}
              variant="outline"
              className="btn-secondary border-[#84A98C] text-[#84A98C] hover:bg-[#84A98C] hover:text-white rounded-full"
            >
              Continue to Urban Nest Quest
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
